import { z } from 'zod/v4';
import { CachedPreviewQueryDTO, CachedQueryDTO } from './cached-query';

const MetricDTO = z.record(z.string(), z.string());

export type Metric = z.infer<typeof MetricDTO>;
const PrometheusValueDTO = z.tuple([z.number(), z.string()]);

const PrometheusMatrixResultDTO = z.object({
  metric: MetricDTO,
  values: z.array(PrometheusValueDTO),
});
const PrometheusVectorResultDTO = z.object({
  metric: MetricDTO,
  value: PrometheusValueDTO,
});

const PrometheusVectorResponseDTO = z.object({
  status: z.literal('success'),
  data: z.object({
    resultType: z.literal('vector'),
    result: z.array(PrometheusVectorResultDTO),
  }),
});
const PrometheusScalarResponseDTO = z.object({
  status: z.literal('success'),
  data: z.object({
    resultType: z.literal('scalar'),
    result: PrometheusValueDTO,
  }),
});
const PrometheusMatrixResponseDTO = z.object({
  status: z.literal('success'),
  data: z.object({
    resultType: z.literal('matrix'),
    result: z.array(PrometheusMatrixResultDTO),
  }),
});

export const PrometheusResponseDTO = z.array(
  z.union([
    PrometheusVectorResponseDTO,
    PrometheusMatrixResponseDTO,
    PrometheusScalarResponseDTO,
  ]),
);
export type PrometheusResponse = z.infer<typeof PrometheusResponseDTO>;

export type PrometheusMatrixResponse = z.infer<
  typeof PrometheusMatrixResponseDTO
>;
export type PrometheusMatrixResult = z.infer<typeof PrometheusMatrixResultDTO>;

export type PrometheusVectorResult = z.infer<typeof PrometheusVectorResultDTO>;

export type PrometheusVectorResponse = z.infer<
  typeof PrometheusVectorResponseDTO
>;
export type PrometheusValue = z.infer<typeof PrometheusValueDTO>;
export type PrometheusScalarResponse = z.infer<
  typeof PrometheusScalarResponseDTO
>;

export const PrometheusQueryPropsDTO = z.object({
  path: z.string(),
  params: z.record(z.string(), z.any()),
});
export type PrometheusQueryProps = z.infer<typeof PrometheusQueryPropsDTO>;

const RangeDTO = z.object({
  start: z.number(),
  end: z.number(),
  step: z.number(),
});

const TimeDTO = z.object({
  time: z.number(),
});

const TemplateDTO = z.object({
  templates: z.union([z.string(), z.array(z.string())]),
});

export const QueryParamsDTO = CachedQueryDTO.pick({
  widget_id: true,
  ci_ids: true,
  advanced_tql_params: true,
  variable_values: true,
}).extend(TimeDTO.shape);

export type QueryParams = z.infer<typeof QueryParamsDTO>;

export const QueryRangeParamsDTO = CachedQueryDTO.pick({
  widget_id: true,
  ci_ids: true,
  advanced_tql_params: true,
  variable_values: true,
}).extend(RangeDTO.shape);

export type QueryRangeParams = z.infer<typeof QueryRangeParamsDTO>;

export const QueryPreviewParamsDTO = CachedPreviewQueryDTO.pick({
  ci_ids: true,
  filter: true,
  advanced_tql_params: true,
  variable_values: true,
}).extend({
  tql_id: z.string(),
  ...TimeDTO.shape,
  ...TemplateDTO.shape,
});

export type QueryPreviewParams = z.infer<typeof QueryPreviewParamsDTO>;

export const QueryRangePreviewParamsDTO = CachedPreviewQueryDTO.pick({
  ci_ids: true,
  filter: true,
  advanced_tql_params: true,
  variable_values: true,
}).extend({
  tql_id: z.string(),
  ...RangeDTO.shape,
  ...TemplateDTO.shape,
});

export type QueryRangePreviewParams = z.infer<
  typeof QueryRangePreviewParamsDTO
>;

export const QueryRangeHashParamsDTO = CachedQueryDTO.pick({
  widget_id: true,
  ci_ids: true,
  advanced_tql_params: true,
  variable_values: true,
}).extend({
  ...RangeDTO.shape,
  template: z.union([z.string(), z.array(z.string())]).optional(),
});

export type QueryRangeHashParams = z.infer<typeof QueryRangeHashParamsDTO>;